// Sprint 16 A5: Recent Activity Timeline — C11: 10 event type icons
import { Trophy, UserPlus, Mail, CheckCircle, Calendar, Star, Phone, CreditCard, MessageSquare, Zap } from "lucide-react";
import type { ActivityItem } from "@/hooks/use-recent-activity";
import { Skeleton, SkeletonAvatar } from "@/components/skeleton";

const TYPE_ICONS: Record<string, { icon: typeof Trophy; color: string }> = {
  deal_won: { icon: Trophy, color: "text-[var(--color-success)]" },
  new_lead: { icon: UserPlus, color: "text-[var(--color-primary)]" },
  email_sent: { icon: Mail, color: "text-[var(--color-ai)]" },
  task_completed: { icon: CheckCircle, color: "text-[var(--color-success)]" },
  booking: { icon: Calendar, color: "text-[var(--color-accent-action)]" },
  review: { icon: Star, color: "text-[var(--color-warning)]" },
  call: { icon: Phone, color: "text-[var(--color-success)]" },
  payment: { icon: CreditCard, color: "text-[var(--color-accent-action)]" },
  message: { icon: MessageSquare, color: "text-[var(--color-ai)]" },
  automation: { icon: Zap, color: "text-[var(--color-warning)]" },
};

interface RecentActivityTimelineProps {
  activities: ActivityItem[] | null;
  isLoading: boolean;
}

export function RecentActivityTimeline({ activities, isLoading }: RecentActivityTimelineProps) {
  if (isLoading || !activities) {
    return (
      <div className="space-y-3">
        {Array.from({ length: 4 }).map((_, i) => (
          <div key={i} className="flex items-center gap-3">
            <SkeletonAvatar size="sm" />
            <div className="flex-1">
              <Skeleton className="mb-1 h-3 w-40" />
              <Skeleton className="h-2 w-16" />
            </div>
          </div>
        ))}
      </div>
    );
  }

  if (activities.length === 0) {
    return <div className="text-sm text-[var(--color-muted-foreground)] text-center py-4">No recent activity</div>;
  }

  return (
    <div className="space-y-3">
      {activities.map((a, i) => {
        const config = TYPE_ICONS[a.type] ?? { icon: CheckCircle, color: "text-[var(--color-muted-foreground)]" };
        const Icon = config.icon;
        return (
          <div key={a.id} className={`data-reveal data-reveal-${Math.min(i + 1, 8)} stagger-item flex items-start gap-3`}>
            <div className={`mt-0.5 rounded-full bg-[var(--color-surface)] p-1.5 ${config.color}`}>
              <Icon className="h-3.5 w-3.5" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm text-[var(--color-foreground)] truncate">{a.title}</p>
              <p className="text-xs text-[var(--color-muted-foreground)]">{a.timestamp}</p>
            </div>
          </div>
        );
      })}
    </div>
  );
}
